import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AITaskResponseResultData } from 'api/collaborateApi';
import { GetItemDetailsResponse } from 'types/api/collaborate';
import { SiteData, SitesAcrossWorkspacesResponse } from 'types/api/site';
import { RootState } from '../store';

export type SelectedSiteInfo = Pick<SiteData, 'id' | 'name'> & {
  users: { name: string; email: string; avatar: string; normal_id: string }[];
  setting?: {
    whiteLabel: {
      favicon: string;
      logo_link: string | null;
      logo?: string | null;
    };
    enabled_collaboration: boolean;
  };
} & { workspace_id?: number };

// {
//   "id": "dnpmaGU3OEFwRjVUSmsvVFhOSGpjUT09",
//   "norm_id": "7",
//   "username": "test@atarim.io",
//   "first_name": "Test",
//   "last_name": null,
//   "email": "test@atarim.io",
//   "avatar": null
// }

export type ActiveUser = {
  id: string;
  norm_id: string;
  username: string;
  first_name: string;
  last_name: string | null;
  email: string | null;
  avatar: string | null;
};

interface SiteInfoState {
  matchingSites: SiteData[] | SitesAcrossWorkspacesResponse;
  selectedId: string | null;
  selectedSiteInfo: SelectedSiteInfo | null;
  selectedSiteCollaborateInfo: GetItemDetailsResponse | null;
  showTaskCountModal?: boolean;
  siteTotalTasks?: number;
  showSiteSelectionModal?: boolean;
  aiTaskData?: AITaskResponseResultData[];
  aiScreenshotFile?: null;
  enabled_collaboration?: boolean;
  activeUsers: Record<string, ActiveUser>;
}

const initialState: SiteInfoState = {
  matchingSites: [],
  selectedId: null,
  selectedSiteInfo: null,
  selectedSiteCollaborateInfo: null,
  showTaskCountModal: false,
  siteTotalTasks: undefined,
  showSiteSelectionModal: false,
  aiTaskData: [],
  aiScreenshotFile: null,
  enabled_collaboration: true,
  activeUsers: {},
};

const siteInfoSlice = createSlice({
  initialState,
  name: 'siteInfo',
  reducers: {
    selectSite: (state, action: PayloadAction<SelectedSiteInfo>) => {
      state.selectedId = action.payload.id;
      state.selectedSiteInfo = action.payload;
    },
    setCollaborateInfo: (
      state,
      action: PayloadAction<GetItemDetailsResponse>
    ) => {
      state.selectedSiteCollaborateInfo = action.payload;
    },
    setMatchingSites: (
      state,
      action: PayloadAction<SiteData[] | SitesAcrossWorkspacesResponse>
    ) => {
      state.matchingSites = action.payload;
    },
    setSiteId: (state, action: PayloadAction<string>) => {
      state.selectedId = action.payload;
    },
    setShowTaskCountModal: (state, action: PayloadAction<boolean>) => {
      state.showTaskCountModal = action.payload;
    },
    setShowSiteSelectionModal: (state, action: PayloadAction<boolean>) => {
      state.showSiteSelectionModal = action.payload;
    },
    setSiteTotalTasks: (state, action: PayloadAction<number | undefined>) => {
      state.siteTotalTasks = action.payload;
    },
    setAiTaskData: (
      state,
      action: PayloadAction<AITaskResponseResultData[]>
    ) => {
      state.aiTaskData = action.payload;
    },
    setAiScreenshotFile: (state, action: PayloadAction<null>) => {
      state.aiScreenshotFile = action.payload;
    },
    removeAiTask: (state, action: PayloadAction<string>) => {
      state.aiTaskData = state.aiTaskData?.filter(
        (task) => task.id !== action.payload
      );
    },
    setEnabledCollaboration: (state, action: PayloadAction<boolean>) => {
      state.enabled_collaboration = action.payload;
    },
    setActiveUsers: (
      state,
      action: PayloadAction<Record<string, ActiveUser>>
    ) => {
      state.activeUsers = action.payload;
    },
    removeActiveUser: (state, action: PayloadAction<string>) => {
      delete state.activeUsers[action.payload];
    },
    addActiveUser: (state, action: PayloadAction<ActiveUser>) => {
      state.activeUsers[action.payload.id] = action.payload;
    },
    clearActiveUsers: (state) => {
      state.activeUsers = {};
    },
  },
});

export const {
  selectSite,
  setCollaborateInfo,
  setMatchingSites,
  setSiteId,
  setShowTaskCountModal,
  setShowSiteSelectionModal,
  setSiteTotalTasks,
  setAiTaskData,
  setAiScreenshotFile,
  removeAiTask,
  setEnabledCollaboration,
  setActiveUsers,
  removeActiveUser,
  addActiveUser,
  clearActiveUsers,
} = siteInfoSlice.actions;
export const getMatchingSites = (state: RootState) =>
  state.siteInfo.matchingSites;
export const getSelectedSiteCollaborateInfo = (state: RootState) =>
  state.siteInfo.selectedSiteCollaborateInfo;
export const getSelectedSiteId = (state: RootState) =>
  state.siteInfo.selectedId;
export const getSelectedSiteInfo = (state: RootState) =>
  state.siteInfo.selectedSiteInfo;
export const getShowTaskCountModal = (state: RootState) =>
  state.siteInfo.showTaskCountModal;
export const getShowSiteSelectionModal = (state: RootState) =>
  state.siteInfo.showSiteSelectionModal;
export const getSiteTotalTasks = (state: RootState) =>
  state.siteInfo.siteTotalTasks;
export const getAiTaskData = (state: RootState) => state.siteInfo.aiTaskData;
export const getAiScreenshotFile = (state: RootState) =>
  state.siteInfo.aiScreenshotFile;
export const getEnabledCollaboration = (state: RootState) =>
  state.siteInfo.enabled_collaboration;
export const getActiveUsers = (state: RootState) => state.siteInfo.activeUsers;
export default siteInfoSlice.reducer;
